/* eslint-disable react/display-name */
import { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardMedia,
  Chip,
  Table,
  Stack,
  TableContainer,
  Typography
} from '@mui/material';
import { MdExpandMore, MdInfoOutline } from 'react-icons/md';
import parse, { DOMNode, Element, domToReact, HTMLReactParserOptions } from 'html-react-parser';
import DOMPurify from 'dompurify';
import { articleContentSx } from './articleContentStyles';

interface HelpArticleContentProps {
  html: string;
}

function isElement(node: DOMNode): node is Element {
  return node.type === 'tag';
}

function getAttr(node: Element, name: string): string | undefined {
  return node.attribs[name];
}

function toDomNodes(children: Element['children']): DOMNode[] {
  return children as DOMNode[];
}

function createReplace(options: HTMLReactParserOptions) {
  let accordionIndex = 0;

  return (node: DOMNode) => {
    if (!isElement(node)) {
      return undefined;
    }

    const { name, children } = node;

    if (name === 'callout-component') {
      const text = children
        .flatMap((child: any) =>
          isElement(child)
            ? child.children.filter((c: any) => c.type === 'text').map((c: any) => c.data)
            : child.type === 'text'
              ? [child.data]
              : []
        )
        .join(' ')
        .trim();

      return (
        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          sx={{
            my: 2,
            px: 2,
            py: 1.5,
            bgcolor: '#f3f3f3',
            color: '#777',
            border: '1px solid #777',
            borderRadius: 2
          }}
        >
          <MdInfoOutline size={20} color='#777' />

          <Typography variant='body2' color='text.primary'>
            {text}
          </Typography>
        </Stack>
      );
    }

    if (name === 'details') {
      const childNodes = toDomNodes(children);
      const summaryNode = childNodes.find((child): child is Element => isElement(child) && child.name === 'summary');
      const contentChildren = childNodes.filter((child) => !(isElement(child) && child.name === 'summary'));
      const accordionKey = `accordion-${accordionIndex++}`;

      return (
        <Accordion
          key={accordionKey}
          disableGutters
          elevation={0}
          sx={{
            my: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: '10px !important',
            overflow: 'hidden',
            '&:before': { display: 'none' },

            '& .MuiAccordionSummary-root': {
              minHeight: 44,
              px: 2,
              py: 0,
              bgcolor: 'transparent',
              transition: 'background-color 0.2s'
            },

            '& .MuiAccordionSummary-root.Mui-expanded': {
              bgcolor: '#ECECEC',
              minHeight: 44
            },
            '& *': {
              color: '#000 !important',
              fontWeight: 'normal'
            },

            '& .MuiAccordionSummary-content': {
              my: 1
            },

            '& .MuiAccordionSummary-content.Mui-expanded': {
              my: 1
            },

            '& .MuiAccordionDetails-root': {
              pt: 1,
              pb: 2,
              px: 2
            }
          }}
        >
          <AccordionSummary expandIcon={<MdExpandMore size={24} />}>
            <Typography>{summaryNode ? domToReact(toDomNodes(summaryNode.children), options) : 'Details'}</Typography>
          </AccordionSummary>

          <AccordionDetails>{domToReact(toDomNodes(contentChildren), options)}</AccordionDetails>
        </Accordion>
      );
    }

    if (name === 'img') {
      const src = getAttr(node, 'src');
      const alt = getAttr(node, 'alt') ?? '';

      if (!src) {
        return null;
      }

      return (
        <Card sx={{ overflow: 'hidden', borderRadius: 3, my: 4 }}>
          <CardMedia component='img' image={src} alt={alt} sx={{ width: '100%' }} />
        </Card>
      );
    }

    if (name === 'table') {
      return (
        <TableContainer sx={{ my: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table
            sx={{
              '& th': {
                backgroundColor: 'grey.100',
                fontWeight: 600
              },
              '& th, & td': {
                padding: '14px',
                border: 1,
                borderColor: 'divider'
              }
            }}
          >
            {domToReact(toDomNodes(children), options)}
          </Table>
        </TableContainer>
      );
    }

    if (name === 'p' && children.some((child: any) => isElement(child) && child.name === 'br')) {
      const badges: string[] = [];
      let current = '';

      children.forEach((child: any) => {
        if (child.type === 'text') {
          current += child.data;
        } else if (isElement(child as any) && child.name === 'strong') {
          current += child.children.map((c: any) => (c.type === 'text' ? c.data : '')).join('');
        } else if (isElement(child) && child.name === 'br') {
          if (current.trim()) {
            badges.push(current.trim());
          }
          current = '';
        }
      });

      if (current.trim()) {
        badges.push(current.trim());
      }

      if (badges.length > 1) {
        return (
          <Stack direction='row' flexWrap='wrap' gap={1} my={2}>
            {badges.map((badge) => (
              <Chip
                key={badge}
                label={badge}
                size='small'
                variant='outlined'
                sx={{ bgcolor: '#ECECEC', color: '#000' }}
              />
            ))}
          </Stack>
        );
      }
    }

    if (name === 'pre') {
      return (
        <Box
          component='span'
          sx={{
            borderRadius: 3,
            bgcolor: 'white !important',
            overflow: 'auto',
            my: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
              color: 'inherit'
            }
          }}
        >
          {domToReact(toDomNodes(children), options)}
        </Box>
      );
    }

    if (name === 'iframe') {
      const src = getAttr(node, 'src');

      if (!src) {
        return null;
      }

      return (
        <Box sx={{ position: 'relative', paddingTop: '56.25%', my: 4, borderRadius: 3, overflow: 'hidden' }}>
          <Box
            component='iframe'
            src={src}
            title={getAttr(node, 'title') ?? 'Embedded content'}
            allow={getAttr(node, 'allow')}
            allowFullScreen={getAttr(node, 'allowfullscreen') !== undefined}
            frameBorder={getAttr(node, 'frameborder') ?? '0'}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
          />
        </Box>
      );
    }

    return undefined;
  };
}

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'callout-component'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'target', 'rel', 'color']
  });
}

export default function HelpArticleContent({ html }: HelpArticleContentProps) {
  const content = useMemo(() => {
    if (!html.trim()) {
      return null;
    }

    const sanitized = sanitizeHtml(html);
    const options: HTMLReactParserOptions = { replace: () => undefined };
    options.replace = createReplace(options);

    return parse(sanitized, options);
  }, [html]);

  if (!content) {
    return null;
  }

  return (
    <Box className='article-content' sx={articleContentSx}>
      {content}
    </Box>
  );
}
